
import { useCallback, useEffect, useMemo, useState } from 'react';
import Link from '@/components/compat/Link';
import { ArrowLeft, FileText, Loader2, Pencil, Plus, Search, Trash2, UserPlus } from 'lucide-react';
import { PainelConfirmDialog } from '@/components/painel/PainelFeedbackDialog';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { TooltipProvider } from '@/components/ui/tooltip';
import { useStaffOnlineStatuses } from '@/hooks/use-staff-online-statuses';
import { cn } from '@/lib/utils';
import type { SiteStaffMember, SiteStaffRole } from '@/lib/cms-types';
import { useCms } from '@/components/painel/cms/cms-context';
import { usePainelText } from '@/components/painel/painel-texts-context';
import {
  COUNTRY_NAMES,
  COUNTRY_SMILIE,
  EquipeMemberCard,
  EquipeRoleSection,
  FLAG_IMG,
  RANK_ICONS,
  STAFF_ROLE_ORDER,
  STAFF_SECTION_LABEL,
  groupStaffByRole,
} from '@/components/help-center/equipe-shared';
import { PainelXatRankField } from '@/components/painel/PainelXatRankField';
import {
  optionIdToXatRank,
  resolveStaffXatRankOptionId,
  type StaffXatRankOptionId,
} from '@/lib/equipe-xat-ranks';
import { painelInputClass, painelSelectTriggerClass, painelTextareaClass } from '@/components/painel/cms/painel-form';
import {
  painelButtonPrimaryClass,
  painelModalCancelButtonClass,
  painelModalLookupButtonClass,
  painelSurfaceClass,
} from '@/components/painel/painel-styles';
import { PainelRocketScroll } from '@/components/painel/PainelRocketScroll';
import { PainelPermissionsDialogShell } from '@/components/painel/permissions/painel-role-visuals';
import { usePainelSaveFeedback } from '@/components/painel/PainelSaveFeedback';
import { painelSelectContentClass } from '@/components/painel/painel-styles';
import { getXatProfile } from '@/services/xat-proxy.service';

/** Detecta se o valor do perfil é URL ou ID numérico de toon xat. */
function normalizeAvatarFromProfile(avatarUrl: string | null | undefined): string {
  const t = String(avatarUrl ?? '').trim();
  if (!t) return '';
  // URL completa
  if (/^https?:\/\//i.test(t) || t.startsWith('//')) return t.startsWith('//') ? `https:${t}` : t;
  // Número puro = toon xat
  if (/^\d+$/.test(t)) return t;
  // Extrai ID de URL web_gear/av/123.png
  const match = t.match(/\/av\/(\d+)\.png/i);
  if (match?.[1]) return match[1];
  return t;
}

function emptyMember(): Omit<SiteStaffMember, 'id'> {
  return {
    name: '',
    username: '',
    xatId: '',
    country: 'BR',
    avatar: '',
    role: 'membros',
  };
}

export function PainelStaffEditorPage() {
  const pageText = usePainelText('equipe');
  const { showSaveSuccess } = usePainelSaveFeedback();
  const { site, setSite, addStaffMember, updateStaffMember, deleteStaffMember, hydrated } =
    useCms();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<Omit<SiteStaffMember, 'id'>>(emptyMember);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [resolving, setResolving] = useState(false);
  const [resolveError, setResolveError] = useState<string | null>(null);
  const [draggingMemberId, setDraggingMemberId] = useState<string | null>(null);
  const [dropTargetRole, setDropTargetRole] = useState<SiteStaffRole | null>(null);
  const [descEditRole, setDescEditRole] = useState<SiteStaffRole | null>(null);
  const [descDraft, setDescDraft] = useState('');
  const [xatRankOptionId, setXatRankOptionId] = useState<StaffXatRankOptionId | 'none'>('none');

  const finalizeDrag = useCallback(() => {
    setDraggingMemberId(null);
    setDropTargetRole(null);
  }, []);

  useEffect(() => {
    window.addEventListener('dragend', finalizeDrag);
    return () => window.removeEventListener('dragend', finalizeDrag);
  }, [finalizeDrag]);

  const dragDropHandlers = useMemo(
    () => ({
      draggingMemberId,
      dropTargetRole,
      onMemberDragStart: (memberId: string) => setDraggingMemberId(memberId),
      onDropZoneDragOver: (targetRole: SiteStaffRole) => setDropTargetRole(targetRole),
      onAssignMemberRole: (memberId: string, targetRole: SiteStaffRole) => {
        const m = site.staffMembers.find((x) => x.id === memberId);
        if (m && m.role !== targetRole) {
          updateStaffMember(memberId, { role: targetRole });
        }
      },
      onDragFinalize: finalizeDrag,
    }),
    [draggingMemberId, dropTargetRole, site.staffMembers, updateStaffMember, finalizeDrag]
  );

  const grouped = useMemo(() => groupStaffByRole(site.staffMembers), [site.staffMembers]);
  const staffXatIds = useMemo(
    () => site.staffMembers.map((m) => String(m.xatId ?? '').trim()).filter(Boolean),
    [site.staffMembers]
  );
  const { byId: onlineById } = useStaffOnlineStatuses(staffXatIds);

  const openCreate = (role?: SiteStaffRole) => {
    setEditingId(null);
    setForm({ ...emptyMember(), ...(role ? { role } : {}) });
    setXatRankOptionId('none');
    setResolveError(null);
    setDialogOpen(true);
  };

  const openEdit = (m: SiteStaffMember) => {
    setEditingId(m.id);
    setForm({
      name: m.name,
      username: m.username,
      xatId: m.xatId,
      country: m.country,
      avatar: m.avatar,
      role: m.role,
    });
    setXatRankOptionId(resolveStaffXatRankOptionId(m));
    setResolveError(null);
    setDialogOpen(true);
  };

  const lookupFromUsername = async () => {
    const u = form.username.trim();
    if (!u) {
      setResolveError('Introduza um username.');
      return;
    }
    setResolving(true);
    setResolveError(null);
    try {
      const j = await getXatProfile({ regname: u });
      if (!j.userId) {
        setResolveError('Não foi possível encontrar este username.');
        return;
      }
      const avatar = normalizeAvatarFromProfile(j.avatarUrl);
      setForm((f) => ({
        ...f,
        xatId: String(j.userId),
        username: j.regname?.trim() || f.username.trim(),
        avatar: avatar || f.avatar,
        name: f.name.trim() || j.regname?.trim() || f.username.trim(),
      }));
    } catch {
      setResolveError('Erro de rede ao resolver o username.');
    } finally {
      setResolving(false);
    }
  };

  const saveMember = () => {
    const name = form.name.trim();
    const username = form.username.trim();
    const xatId = form.xatId.trim();
    if (!name || !username || !xatId) return;
    const xatRank = optionIdToXatRank(xatRankOptionId);
    const payload: Omit<SiteStaffMember, 'id'> = {
      name,
      username,
      xatId,
      country: form.country === 'PT' ? 'PT' : 'BR',
      avatar: form.avatar.trim(),
      role: form.role,
      ...(xatRank ? { xatRank } : {}),
    };
    if (editingId) {
      updateStaffMember(editingId, xatRank ? payload : ({ ...payload, xatRank: undefined }));
    } else {
      addStaffMember(payload);
    }
    setDialogOpen(false);
    showSaveSuccess({
      title: editingId ? 'Membro atualizado' : 'Membro adicionado',
      description: 'A equipa na página pública foi actualizada.',
    });
  };

  const selectTriggerClass = painelSelectTriggerClass;

  const canSave =
    form.name.trim().length > 0 &&
    form.username.trim().length > 0 &&
    form.xatId.trim().length > 0;

  return (
    <TooltipProvider delayDuration={250}>
      <div className="space-y-8">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <Button
              variant="ghost"
              asChild
              className="mb-2 -ml-2 rounded-xl text-slate-300 hover:bg-white/10 hover:text-white"
            >
              <Link href="/painel/paginas">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Páginas
              </Link>
            </Button>
            <h1 className="font-poppins text-2xl font-bold tracking-tight text-white md:text-3xl">
              {pageText.title}
            </h1>
            <p className="mt-1 font-inter text-sm text-slate-400">{pageText.description}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button asChild variant="outline" className="rounded-xl border-white/15 text-blue-100 hover:bg-white/10">
              <Link href="/painel/paginas/equipe">
                <FileText className="mr-2 h-4 w-4" />
                Textos da página
              </Link>
            </Button>
            <Button
              type="button"
              onClick={() => openCreate()}
              className={cn(
                'rounded-xl border border-sky-400/40 bg-sky-500/90 font-poppins text-white shadow-lg hover:bg-sky-400'
              )}
            >
              <Plus className="mr-2 h-4 w-4" />
              Novo membro
            </Button>
          </div>
        </div>

        <div
          className={cn(
            painelSurfaceClass,
            'space-y-5 p-5 md:p-6',
            !hydrated && 'pointer-events-none opacity-70'
          )}
        >
          <div>
            <h2 className="font-poppins text-base font-semibold text-white">
              Cabeçalho da página
            </h2>
            <p className="mt-0.5 font-inter text-sm text-slate-400">
              É o que aparece no topo da página da equipe: o nome grande e a frase de
              boas-vindas logo abaixo.
            </p>
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="grid gap-2">
              <Label htmlFor="staff-hero-title-edit" className="text-blue-100/90">
                Título
              </Label>
              <input
                id="staff-hero-title-edit"
                type="text"
                value={site.staffPageTitle}
                onChange={(e) => setSite({ ...site, staffPageTitle: e.target.value })}
                placeholder="Ex.: Nossa Equipe"
                autoComplete="off"
                className={painelInputClass}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="staff-hero-sub-edit" className="text-blue-100/90">
                Frase de boas-vindas
              </Label>
              <textarea
                id="staff-hero-sub-edit"
                value={site.staffPageSubtitle}
                onChange={(e) => setSite({ ...site, staffPageSubtitle: e.target.value })}
                placeholder="Ex.: Conheça as pessoas que cuidam da nossa comunidade."
                rows={3}
                className={cn(painelTextareaClass, 'min-h-[80px] resize-y py-2')}
              />
            </div>
          </div>
        </div>

        <div className={cn('space-y-14 py-2', !hydrated && 'opacity-70')}>
          {!hydrated ? (
            <div className="py-16 text-center font-inter text-sm text-slate-400">
              Carregando…
            </div>
          ) : (
            <>
              {STAFF_ROLE_ORDER.map((role) => (
                <EquipeRoleSection
                  key={role}
                  role={role}
                  members={grouped[role]}
                  sectionTone="painel"
                  roleDescriptionText={site.staffRoleDescriptions[role]}
                  onAddMember={(r) => openCreate(r)}
                  onlineById={onlineById}
                  rankHeaderExtra={
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      draggable={false}
                      className="h-9 rounded-xl border-white/15 bg-white/5 px-3 font-poppins text-xs text-blue-100 hover:bg-white/10"
                      onClick={() => {
                        setDescEditRole(role);
                        setDescDraft(site.staffRoleDescriptions[role] ?? '');
                      }}
                    >
                      <Pencil className="mr-1.5 h-3.5 w-3.5 shrink-0" />
                      Editar
                    </Button>
                  }
                  dragDrop={dragDropHandlers}
                  renderCard={(member, rankIconSrc, rankLabel) => (
                      <EquipeMemberCard
                        member={member}
                        rankIconSrc={rankIconSrc}
                        rankLabel={rankLabel}
                        onlinePresence={
                          onlineById[String(member.xatId ?? '').trim().toLowerCase()]?.presence
                        }
                        adminSlot={
                          <div className="absolute right-2 top-2 z-20 flex gap-1">
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              draggable={false}
                              className="h-8 border-white/20 bg-slate-900/80 px-2 text-xs text-white hover:bg-slate-800"
                              onClick={() => openEdit(member)}
                            >
                              <Pencil className="mr-1 h-3 w-3" />
                              Editar
                            </Button>
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              draggable={false}
                              className="h-8 border-rose-500/40 bg-slate-900/80 px-2 text-xs text-rose-200 hover:bg-rose-950/50"
                              onClick={() => setDeleteId(member.id)}
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                        }
                      />
                    )}
                  />
                ))}
            </>
          )}
        </div>

        <Dialog
          open={descEditRole !== null}
          onOpenChange={(open) => !open && setDescEditRole(null)}
        >
          <DialogContent
            showCloseButton={false}
            className="max-h-[90vh] max-w-lg overflow-hidden border-0 bg-[#030023] p-0 shadow-none"
          >
            <DialogTitle className="sr-only">
              Descrição do cargo
              {descEditRole ? ` — ${STAFF_SECTION_LABEL[descEditRole]}` : ''}
            </DialogTitle>
            <PainelPermissionsDialogShell>
              <div className="border-b border-white/10 bg-blue-950/20 px-6 pb-5 pt-6 sm:px-8">
                <div className="flex items-start gap-4 pr-14">
                  <span className="inline-flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-sky-500/25 to-sky-700/10 ring-1 ring-sky-400/35 shadow-[0_0_40px_-10px_rgba(56,189,248,0.35)]">
                    <FileText className="h-7 w-7 text-sky-200" aria-hidden />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="font-inter text-[10px] font-bold uppercase tracking-[0.18em] text-sky-300/55">
                      Equipe
                    </p>
                    <h2 className="mt-1 font-poppins text-xl font-bold text-white sm:text-2xl">
                      Descrição do cargo
                    </h2>
                    <p className="mt-1.5 font-inter text-sm text-slate-400">
                      {descEditRole ? (
                        <>
                          Texto exibido na caixa de{' '}
                          <span className="font-medium text-slate-200">
                            {STAFF_SECTION_LABEL[descEditRole]}
                          </span>{' '}
                          na página pública <span className="text-slate-300">/equipe</span>.
                        </>
                      ) : null}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-[#030023]/30 px-6 py-5 sm:px-8">
                <div className="mb-4 rounded-xl border border-sky-400/20 bg-blue-950/40 px-4 py-3 font-inter text-xs leading-relaxed text-slate-400">
                  Use uma linha em branco entre parágrafos. Cada bloco vira um item na caixa do
                  cargo.
                </div>
                <div className="grid gap-2">
                  <Label className="font-inter text-xs font-medium text-blue-100/90">
                    Conteúdo
                  </Label>
                  <textarea
                    value={descDraft}
                    onChange={(e) => setDescDraft(e.target.value)}
                    className={cn(painelTextareaClass, 'min-h-[180px]')}
                    placeholder={'Primeiro parágrafo…\n\nSegundo parágrafo…'}
                  />
                </div>
              </div>

              <div className="flex flex-col-reverse gap-2 border-t border-white/10 bg-blue-950/25 px-6 py-4 sm:flex-row sm:justify-end sm:gap-3 sm:px-8">
                <Button
                  type="button"
                  variant="ghost"
                  className={painelModalCancelButtonClass}
                  onClick={() => setDescEditRole(null)}
                >
                  Cancelar
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  className={cn(
                    painelButtonPrimaryClass,
                    'h-10 px-5 ring-0 ring-offset-0 focus-visible:ring-2 focus-visible:ring-sky-400/35 focus-visible:ring-offset-0'
                  )}
                  disabled={!descEditRole}
                  onClick={() => {
                    if (!descEditRole) return;
                    setSite({
                      ...site,
                      staffRoleDescriptions: {
                        ...site.staffRoleDescriptions,
                        [descEditRole]: descDraft,
                      },
                    });
                    setDescEditRole(null);
                    showSaveSuccess({
                      title: 'Descrição guardada',
                      description: 'O texto do cargo foi actualizado na página /equipe.',
                    });
                  }}
                >
                  Salvar
                </Button>
              </div>
            </PainelPermissionsDialogShell>
          </DialogContent>
        </Dialog>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent
            showCloseButton={false}
            className="max-h-[90vh] max-w-2xl overflow-hidden border-0 bg-[#030023] p-0 shadow-none"
          >
            <DialogTitle className="sr-only">
              {editingId ? 'Editar membro' : 'Novo membro'}
            </DialogTitle>
            <PainelPermissionsDialogShell>
              <div className="border-b border-white/10 bg-blue-950/20 px-6 pb-5 pt-6 sm:px-8">
                <div className="flex items-start gap-4 pr-14">
                  <span className="inline-flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-sky-500/25 to-sky-700/10 ring-1 ring-sky-400/35 shadow-[0_0_40px_-10px_rgba(56,189,248,0.35)]">
                    <UserPlus className="h-7 w-7 text-sky-200" aria-hidden />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="font-inter text-[10px] font-bold uppercase tracking-[0.18em] text-sky-300/55">
                      Equipe
                    </p>
                    <h2 className="mt-1 font-poppins text-xl font-bold text-white sm:text-2xl">
                      {editingId ? 'Editar membro' : 'Novo membro'}
                    </h2>
                    <p className="mt-1.5 font-inter text-sm text-slate-400">
                      Os dados abaixo espelham o cartão na página pública{' '}
                      <span className="text-slate-300">/equipe</span>.
                    </p>
                  </div>
                </div>
              </div>

              <PainelRocketScroll
                className='max-h-[min(56vh,520px)] bg-[#030023]/30'
                viewportClassName='px-6 py-5 sm:px-8 scroll-smooth'
              >
                <div className="mb-5 rounded-xl border border-sky-400/20 bg-blue-950/40 px-4 py-3 font-inter text-xs leading-relaxed text-slate-400">
                  Busque o username para preencher o xat ID e o avatar automaticamente. O link
                  xatspace será <span className="text-slate-300">xat.me/username</span>. Se o avatar
                  for URL, usa a imagem; se for número, usa o toon do xat.
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="grid gap-2 sm:col-span-2">
                    <Label className="font-inter text-xs font-medium text-blue-100/90">
                      Username (xat)
                    </Label>
                    <div className="flex gap-2">
                      <input
                        value={form.username}
                        onChange={(e) => setForm((f) => ({ ...f, username: e.target.value }))}
                        className={cn(painelInputClass, 'min-w-0 flex-1')}
                        placeholder="Ex.: Cupim"
                        autoComplete="off"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        className={painelModalLookupButtonClass}
                        onClick={() => void lookupFromUsername()}
                        disabled={resolving}
                      >
                        {resolving ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <>
                            <Search className="h-3.5 w-3.5" aria-hidden />
                            Buscar ID
                          </>
                        )}
                      </Button>
                    </div>
                    {resolveError ? (
                      <p className="font-inter text-xs text-rose-300">{resolveError}</p>
                    ) : null}
                  </div>

                  <div className="grid gap-2">
                    <Label className="font-inter text-xs font-medium text-blue-100/90">xat ID</Label>
                    <input
                      value={form.xatId}
                      onChange={(e) => setForm((f) => ({ ...f, xatId: e.target.value }))}
                      className={painelInputClass}
                      placeholder="Preenchido pela busca"
                      inputMode="numeric"
                    />
                  </div>

                  <div className="grid gap-2">
                    <Label className="font-inter text-xs font-medium text-blue-100/90">
                      Nome ao lado do peão
                    </Label>
                    <input
                      value={form.name}
                      onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                      className={painelInputClass}
                      placeholder="Ex.: Cupim"
                    />
                  </div>

                  <div className="grid gap-2">
                    <Label className="font-inter text-xs font-medium text-blue-100/90">País</Label>
                    <Select
                      value={form.country}
                      onValueChange={(v: 'BR' | 'PT') => setForm((f) => ({ ...f, country: v }))}
                    >
                      <SelectTrigger className={selectTriggerClass}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className={painelSelectContentClass}>
                        {(['BR', 'PT'] as const).map((code) => (
                          <SelectItem
                            key={code}
                            value={code}
                            className="focus:bg-white/10 focus:text-white"
                          >
                            <span className="flex items-center gap-2">
                              <img
                                src={FLAG_IMG[code]}
                                alt=""
                                className="h-5 w-auto object-contain"
                              />
                              <span className="font-mono text-xs text-slate-200">
                                {COUNTRY_SMILIE[code]}
                              </span>
                              <span>{COUNTRY_NAMES[code]}</span>
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="grid gap-2">
                    <Label className="font-inter text-xs font-medium text-blue-100/90">Cargo</Label>
                    <Select
                      value={form.role}
                      onValueChange={(v: SiteStaffRole) => setForm((f) => ({ ...f, role: v }))}
                    >
                      <SelectTrigger className={selectTriggerClass}>
                        <SelectValue>
                          <span className="flex items-center gap-1">
                            <img
                              src={RANK_ICONS[form.role]}
                              alt=""
                              className="h-5 w-5 object-contain"
                              aria-hidden
                            />
                            <span className="-ml-1 leading-none">{STAFF_SECTION_LABEL[form.role]}</span>
                          </span>
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent className={painelSelectContentClass}>
                        {STAFF_ROLE_ORDER.map((r) => (
                          <SelectItem
                            key={r}
                            value={r}
                            className="focus:bg-white/10 focus:text-white"
                          >
                            <span className="flex items-center gap-1">
                              <img
                                src={RANK_ICONS[r]}
                                alt=""
                                className="h-5 w-5 object-contain"
                                aria-hidden
                              />
                              <span className="-ml-1 leading-none">{STAFF_SECTION_LABEL[r]}</span>
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="grid gap-2 sm:col-span-2">
                    <PainelXatRankField
                      value={xatRankOptionId}
                      onChange={setXatRankOptionId}
                    />
                  </div>
                </div>
              </PainelRocketScroll>

              <div className="flex flex-col-reverse gap-2 border-t border-white/10 bg-blue-950/25 px-6 py-4 sm:flex-row sm:justify-end sm:gap-3 sm:px-8">
                <Button
                  type="button"
                  variant="ghost"
                  className={painelModalCancelButtonClass}
                  onClick={() => setDialogOpen(false)}
                >
                  Cancelar
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  className={cn(painelButtonPrimaryClass, 'h-10 px-5 ring-0 ring-offset-0 focus-visible:ring-2 focus-visible:ring-sky-400/35 focus-visible:ring-offset-0')}
                  onClick={saveMember}
                  disabled={!canSave}
                >
                  {editingId ? 'Salvar alterações' : 'Adicionar membro'}
                </Button>
              </div>
            </PainelPermissionsDialogShell>
          </DialogContent>
        </Dialog>

        <PainelConfirmDialog
          open={deleteId !== null}
          onOpenChange={(o) => !o && setDeleteId(null)}
          title="Remover membro?"
          description="Ele deixará de aparecer na página pública da equipe."
          confirmLabel="Remover"
          onConfirm={() => {
            if (deleteId) deleteStaffMember(deleteId);
            setDeleteId(null);
          }}
        />
      </div>
    </TooltipProvider>
  );
}
